import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs, doc, getDoc, query, where, orderBy, addDoc, updateDoc, setDoc, arrayUnion } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import IconRenderer from '../ui/IconRenderer';
import LevelingSystem from '../SRS/LevelingSystem';
import { fetchCookbookSaveQuota, messageForCookbookQuotaCheckUnavailable } from '../../services/subscriptionQuotaService';
import {
  confirmCookbookDeckNotRemovedByQuotaTrigger,
  cookbookDeckRemovedByMonthlyLimitMessage,
} from '../../utils/cookbookDeckQuotaTriggerWatch';
import './CourseViewer.css';

const CourseViewer = ({ course, userId, onBack, onProgressUpdate, onModeChange, onDeleteCourse, onStartStudying, onDeckCreated }) => {
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [currentSubLessonIndex, setCurrentSubLessonIndex] = useState(0);
  const [lessonSubLessonStates, setLessonSubLessonStates] = useState({}); // Track sub-lesson selection per lesson
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [expandedLessons, setExpandedLessons] = useState(new Set([0])); // Start with first lesson expanded
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conceptExplanations, setConceptExplanations] = useState({});
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [allLessons, setAllLessons] = useState([]); // Store all lessons loaded from Firestore
  // Concept cards state
  const [conceptCardsBySubLesson, setConceptCardsBySubLesson] = useState({});
  const [currentCardIndexBySubLesson, setCurrentCardIndexBySubLesson] = useState({});
  const [understoodCardsBySubLesson, setUnderstoodCardsBySubLesson] = useState({});
  // Import to deck functionality
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [levelingSystem] = useState(new LevelingSystem(userId));
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagMenu, setTagMenu] = useState({ open: false, tag: '', rename: '' });

  // Course leveling state
  const [courseLevel, setCourseLevel] = useState(course.level || 1);
  const [courseExp, setCourseExp] = useState(course.current_exp || 0);
  const [courseExpToLevel, setCourseExpToLevel] = useState(course.exp_to_level || levelingSystem.calculateExpToLevel(2));

  // Flashcard state
  const [isFlipped, setIsFlipped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Syllabus view state
  const [showSyllabusView, setShowSyllabusView] = useState(false);

  // Layout calculation state
  const [layoutCalculated, setLayoutCalculated] = useState(false);
  const [flashcardHeight, setFlashcardHeight] = useState(600);

  const [hasOverlap, setHasOverlap] = useState(false);

  // Helper function to calculate experience required for leveling (matches LevelingSystem.js)
  const calculateExpToLevel = (level) => {
    return Math.round(100 * Math.pow(10, (level - 1) / (99 / Math.log(10))));
  };

  // Use allLessons if available, otherwise fall back to course.syllabus
  const syllabus = allLessons.length > 0 ? allLessons : (course.syllabus || []);
  const currentLesson = syllabus && syllabus.length > 0 ? syllabus[currentLessonIndex] : null;
  const currentSubLesson = currentLesson?.subLessons?.[currentSubLessonIndex];

  // Helper function to get the current concept explanation
  const getCurrentConceptExplanation = () => {
    if (!currentSubLesson) return null;

    console.log('Getting concept explanation for sub-lesson:', {
      id: currentSubLesson.id,
      title: currentSubLesson.title,
      type: currentSubLesson.type,
      hasConceptExplanation: !!currentSubLesson.conceptExplanation
    });

    // First check if the sub-lesson already has the explanation
    if (currentSubLesson.conceptExplanation) {
      console.log('Found explanation in sub-lesson:', currentSubLesson.conceptExplanation.substring(0, 100) + '...');
      return currentSubLesson.conceptExplanation;
    }

    // Then check our local state
    const explanationKey = `${currentLesson?.id}_${currentSubLesson.id}`;
    const explanation = conceptExplanations[explanationKey];
    if (explanation) {
      console.log('Found explanation in local state:', explanation.substring(0, 100) + '...');
      return explanation;
    }

    console.log('No explanation found for key:', explanationKey);
    return null;
  };

  // Utility: split long explanation into digestible cards
  const splitExplanationIntoCards = (text) => {
    if (!text) return [];
    // Normalize whitespace but preserve code fences content blocks
    const segments = [];
    const lines = String(text).split('\n');
    let buffer = [];
    let inCodeFence = false;
    lines.forEach((line) => {
      if (line.trim().startsWith('```')) {
        inCodeFence = !inCodeFence;
      }
      buffer.push(line);
      // When not in code, split on blank line to form paragraphs
      if (!inCodeFence && line.trim() === '') {
        segments.push(buffer.join('\n').trim());
        buffer = [];
      }
    });
    if (buffer.length) segments.push(buffer.join('\n').trim());

    // Further split long paragraphs into ~400-600 char chunks by sentence
    const cards = [];
    const sentenceSplitter = /(?<=[.!?])\s+(?=[A-Z0-9"'`(])/g;
    segments.forEach((seg) => {
      if (!seg) return;
      if (seg.includes('```')) {
        cards.push(seg);
        return;
      }
      const sentences = seg.split(sentenceSplitter);
      let chunk = '';
      sentences.forEach((s, idx) => {
        const tentative = chunk ? `${chunk} ${s}` : s;
        if (tentative.length > 520 && chunk) {
          cards.push(chunk.trim());
          chunk = s;
        } else {
          chunk = tentative;
        }
        // flush at end
        if (idx === sentences.length - 1 && chunk) {
          cards.push(chunk.trim());
          chunk = '';
        }
      });
    });
    return cards.filter(Boolean);
  };

  // Build concept cards whenever the current explanation changes
  useEffect(() => {
    const explanation = getCurrentConceptExplanation();
    if (!currentSubLesson || !explanation) return;
    const key = `${currentLesson?.id}_${currentSubLesson.id}`;
    // Only compute once per sub-lesson
    if (!conceptCardsBySubLesson[key]) {
      const cards = splitExplanationIntoCards(explanation);
      setConceptCardsBySubLesson((prev) => ({ ...prev, [key]: cards }));
      setCurrentCardIndexBySubLesson((prev) => ({ ...prev, [key]: 0 }));
      setUnderstoodCardsBySubLesson((prev) => ({ ...prev, [key]: new Set() }));
    }
  }, [currentLesson, currentSubLesson, conceptExplanations, forceUpdate]);

  const getCardNavState = () => {
    if (!currentSubLesson) return { cards: [], index: 0, key: '' };
    const key = `${currentLesson?.id}_${currentSubLesson.id}`;
    const cards = conceptCardsBySubLesson[key] || [];
    const index = currentCardIndexBySubLesson[key] || 0;
    return { cards, index, key };
  };

  const goToPrevCard = () => {
    const { cards, index, key } = getCardNavState();
    if (cards.length === 0) return;
    const next = Math.max(0, index - 1);
    setCurrentCardIndexBySubLesson((prev) => ({ ...prev, [key]: next }));
  };

  const goToNextCard = () => {
    const { cards, index, key } = getCardNavState();
    if (cards.length === 0) return;
    const next = Math.min(cards.length - 1, index + 1);
    setCurrentCardIndexBySubLesson((prev) => ({ ...prev, [key]: next }));
  };

  const saveUnderstoodCardsToFirebase = async (key, understoodCards) => {
    try {
      const courseRef = doc(db, `users/${userId}/courses/${course.id}`);
      await updateDoc(courseRef, {
        [`progress.understoodCards.${key}`]: Array.from(understoodCards),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving understood cards:', error);
      // Optionally show user feedback about save failure
    }
  };

  const markCurrentCardUnderstood = () => {
    const { cards, index, key } = getCardNavState();
    if (!cards.length) return;
    setUnderstoodCardsBySubLesson((prev) => {
      const existing = new Set(prev[key] || new Set());
      if (existing.has(index)) {
        existing.delete(index);
      } else {
        existing.add(index);

        // Check if all cards are now understood
        if (existing.size === cards.length) {
          // Auto-complete the lesson after a short delay
          setTimeout(() => {
            handleComplete();
          }, 500);
        }
      }

      // Save to Firebase
      saveUnderstoodCardsToFirebase(key, existing);

      return { ...prev, [key]: existing };
    });
  };

  useEffect(() => {
    if (course.progress?.currentLesson !== undefined) {
      setCurrentLessonIndex(course.progress.currentLesson);
    }
    if (course.progress?.currentSubLesson !== undefined) {
      setCurrentSubLessonIndex(course.progress.currentSubLesson);
    }
  }, [course.progress]);

  // Load understood cards from Firebase when course changes
  useEffect(() => {
    if (course.progress?.understoodCards) {
      const loadedUnderstoodCards = {};
      Object.entries(course.progress.understoodCards).forEach(([key, cardIndices]) => {
        // Ensure cardIndices is an array before creating Set
        if (Array.isArray(cardIndices)) {
          loadedUnderstoodCards[key] = new Set(cardIndices);
        }
      });
      setUnderstoodCardsBySubLesson(loadedUnderstoodCards);
    }
  }, [course.id]);

  // Load existing concept explanations when component mounts
  useEffect(() => {
    if (syllabus) {
      const existingExplanations = {};
      syllabus.forEach(lesson => {
        lesson.subLessons?.forEach(subLesson => {
          if (subLesson.type === 'concept' && subLesson.conceptExplanation) {
            const key = `${lesson.id}_${subLesson.id}`;
            existingExplanations[key] = subLesson.conceptExplanation;
          }
        });
      });
      setConceptExplanations(existingExplanations);
      console.log('Loaded existing concept explanations:', existingExplanations);
    }
  }, [syllabus]);

  // Load all lessons from Firestore when component mounts
  useEffect(() => {
    loadAllLessons();
  }, [course.id, userId]);

  // Mark course as opened (for "In Progress" / last-interacted ordering on Home)
  useEffect(() => {
    if (!course?.id || !userId) return;
    const courseRef = doc(db, `users/${userId}/courses/${course.id}`);
    const now = new Date();
    updateDoc(courseRef, { lastAccessed: now, updatedAt: now }).catch((err) =>
      console.warn('Failed to update course lastAccessed', err)
    );
  }, [course?.id, userId]);

  // Update course level when component mounts
  useEffect(() => {
    updateCourseLevel();
  }, [course.id, userId]);

  // Reset flashcard flip state when changing sub-lessons (keep expanded state)
  useEffect(() => {
    console.log('Resetting flip state due to sub-lesson change:', {
      currentLessonIndex,
      currentSubLessonIndex,
      isFlipped,
      isExpanded
    });
    setIsFlipped(false);
  }, [currentLessonIndex, currentSubLessonIndex]);

  // Calculate layout when component mounts or content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateLayout();
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, [currentLessonIndex, currentSubLessonIndex, isExpanded, allLessons]); // Removed isFlipped to prevent recalculation when flipping in expanded state

  // Check for overlaps after layout calculation
  useEffect(() => {
    if (layoutCalculated) {
      const timer = setTimeout(() => {
        checkOverlap();
      }, 200); // Check after layout has settled

      return () => clearTimeout(timer);
    }
  }, [layoutCalculated, flashcardHeight]);

  // Continuous overlap monitoring with immediate adjustment
  useEffect(() => {
    const interval = setInterval(() => {
      if (layoutCalculated) {
        const isOverlapping = checkOverlap();
        if (isOverlapping) {
          // Force immediate recalculation with smaller height
          const currentHeight = flashcardHeight;
          const newHeight = Math.max(currentHeight * 0.9, 400); // Reduce by 10% or min 400px
          if (newHeight < currentHeight) {
            setFlashcardHeight(newHeight);
            console.log('Emergency height reduction:', { from: currentHeight, to: newHeight });
          }
        }
      }
    }, 500); // Check every 500ms for more responsive adjustment

    return () => clearInterval(interval);
  }, [layoutCalculated, flashcardHeight]);

  // Recalculate layout on window resize
  useEffect(() => {
    const handleResize = () => {
      calculateLayout();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update course level based on snippet progress
  const updateCourseLevel = async () => {
    try {
      // Get all course snippets
      const cards = await fetchAllCourseCards();

      if (cards.length === 0) {
        return;
      }

      // Calculate average level and total experience
      let totalLevel = 0;
      let totalExp = 0;
      let snippetCount = 0;

      cards.forEach(card => {
        if (card.level !== undefined) {
          totalLevel += card.level;
          totalExp += card.current_exp || 0;
          snippetCount++;
        }
      });

      if (snippetCount === 0) {
        return;
      }

      const averageLevel = Math.round(totalLevel / snippetCount);
      const courseExp = Math.round(totalExp / snippetCount); // Average exp per snippet
      const courseExpToLevel = levelingSystem.calculateExpToLevel(averageLevel + 1);

      // Update course leveling fields
      const courseRef = doc(db, `users/${userId}/courses/${course.id}`);
      await updateDoc(courseRef, {
        level: averageLevel,
        current_exp: courseExp,
        exp_to_level: courseExpToLevel,
        updatedAt: new Date()
      });

      // Update local state
      setCourseLevel(averageLevel);
      setCourseExp(courseExp);
      setCourseExpToLevel(courseExpToLevel);

      console.log(`Updated course level: ${averageLevel}, exp: ${courseExp}/${courseExpToLevel}`);
    } catch (error) {
      console.error('Error updating course level:', error);
    }
  };

  // Auto-generate concept explanation when user clicks on any sub-lesson
  useEffect(() => {
    if (currentSubLesson &&
      !getCurrentConceptExplanation() &&
      !generatingExplanation) {

      console.log('No existing explanation found, generating new one for:', {
        subLessonId: currentSubLesson.id,
        subLessonTitle: currentSubLesson.title,
        subLessonType: currentSubLesson.type
      });
      generateConceptExplanation();
    }
  }, [currentSubLesson, currentLesson, conceptExplanations, generatingExplanation]);



  const loadAllLessons = async () => {
    try {
      console.log('Loading all lessons from Firestore for course:', course.id);

      // Load all lessons from Firestore
      const lessonsRef = collection(db, `users/${userId}/courses/${course.id}/lessons`);
      const q = query(lessonsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      const lessons = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('Loaded lessons from Firestore:', lessons);
      setAllLessons(lessons);
    } catch (error) {
      console.error('Error loading lessons from Firestore:', error);
      // Fall back to course.syllabus if Firestore loading fails
      setAllLessons(course.syllabus || []);
    }
  };

  // Extract language from course title or use course.language
  const extractCourseLanguage = (course) => {
    if (course.language) {
      return normalizeLanguage(course.language);
    }

    if (course.title) {
      const title = course.title.toLowerCase();
      if (title.includes('javascript')) return 'javascript';
      if (title.includes('python')) return 'python';
      if (title.includes('java') && !title.includes('javascript')) return 'java';
      if (title.includes('c++')) return 'cpp';
      if (title.includes('c#')) return 'csharp';
      if (title.includes('ruby')) return 'ruby';
      if (title.includes('typescript')) return 'typescript';
      if (title.includes('go') || title.includes('golang')) return 'go';
      if (title.includes('php')) return 'php';
      if (title.includes('rust')) return 'rust';
      if (title.includes('kotlin')) return 'kotlin';
      if (title.includes('swift')) return 'swift';
    }

    return 'javascript'; // Default fallback
  };

  // Ensure all sub-lessons have the correct language
  const ensureSubLessonLanguage = (subLesson) => {
    if (!subLesson.language) {
      subLesson.language = extractCourseLanguage(course);
    }
    return subLesson;
  };

  // Normalize language to the editor/runtime ids used by StudyMode/Monaco
  const normalizeLanguage = (lang) => {
    if (!lang) return 'javascript';
    const l = String(lang).toLowerCase();
    if (l.includes('c++') || l === 'cpp') return 'cpp';
    if (l.includes('c#') || l === 'csharp') return 'csharp';
    if (l === 'js' || l.includes('javascript')) return 'javascript';
    if (l === 'ts' || l.includes('typescript')) return 'typescript';
    if (l.includes('python') || l === 'py') return 'python';
    if (l.includes('java')) return 'java';
    if (l.includes('ruby')) return 'ruby';
    if (l === 'go' || l.includes('golang')) return 'go';
    if (l.includes('php')) return 'php';
    if (l.includes('rust')) return 'rust';
    if (l.includes('kotlin')) return 'kotlin';
    if (l.includes('swift')) return 'swift';
    return l;
  };

  /** Cards from course syllabus only (legacy users/{uid}/snippets course rows removed). */
  const fetchAllCourseCards = async () => {
    const lessonsToProcess = allLessons.length > 0 ? allLessons : (course.syllabus || []);
    return buildCardsFromCourseSubLessons(lessonsToProcess);
  };

  const fetchLessonCards = async (lessonId) => {
    const lessonsToProcess = allLessons.length > 0 ? allLessons : (course.syllabus || []);
    const lesson = lessonsToProcess.find((l) => l.id === lessonId);
    if (!lesson) return [];
    return buildCardsFromLessonSubLessons(lesson);
  };

  const buildCardsFromLessonSubLessons = (lesson) => {
    if (!lesson || !Array.isArray(lesson.subLessons)) return [];
    return lesson.subLessons
      .filter(sl => !!sl.codeSnippet)
      .map(sl => {
        const extractedCourseLanguage = extractCourseLanguage(course);
        const originalLanguage = sl.language || course.language || extractedCourseLanguage;
        const normalizedLanguage = normalizeLanguage(originalLanguage);

        console.log('Building card from sub-lesson:', {
          title: sl.title || 'Untitled',
          originalLanguage,
          normalizedLanguage,
          courseLanguage: course.language,
          extractedCourseLanguage
        });

        return {
          id: sl.id,
          title: sl.title || 'Untitled',
          description: sl.description || '',
          solution: sl.codeSnippet,
          language: normalizedLanguage,
          tags: [],
          originalSnippetId: sl.id
        };
      });
  };

  const buildCardsFromCourseSubLessons = (lessons) => {
    const source = Array.isArray(lessons) && lessons.length ? lessons : syllabus || [];
    const cards = [];
    source.forEach(lesson => {
      const lessonCards = buildCardsFromLessonSubLessons(lesson);
      cards.push(...lessonCards);
    });
    return cards;
  };

  const createCookbookFromCourse = async (course) => {
    try {
      console.log('Creating cookbook for course:', course.id);

      const quota = await fetchCookbookSaveQuota();
      if (quota.quotaCheckUnavailable) {
        throw new Error(messageForCookbookQuotaCheckUnavailable(quota));
      }
      if (!quota.isPremium && quota.allowed === false) {
        throw new Error(
          `Free accounts can create up to ${quota.limit} new cookbooks per month (UTC). Upgrade to Pro for unlimited cookbooks and full features.`
        );
      }

      const courseLanguage = course.language || 'javascript';
      const courseTopic = course.topic || 'general_programming';
      const timestamp = new Date();

      // Create the cookbook document
      const cookbookData = {
        name: `${course.title} - Code Cookbook`,
        description: `A collection of code snippets from the "${course.title}" course. Practice these examples to master ${courseLanguage} programming concepts.`,
        language: courseLanguage,
        type: 'cookbook',
        tags: [courseTopic, course.difficulty, 'course', 'cookbook'].filter(tag => tag !== undefined && tag !== null),
        // Course-specific metadata
        courseId: course.id,
        courseTitle: course.title,
        // Initialize leveling fields
        level: 1,
        current_exp: 0,
        exp_to_level: calculateExpToLevel(2),
        // Timestamps
        createdAt: timestamp,
        updatedAt: timestamp,
        lastAccessed: timestamp,
        userId: userId
      };

      const decksRef = collection(db, `users/${userId}/decks`);
      const cookbookRef = await addDoc(decksRef, cookbookData);
      const cookbookId = cookbookRef.id;

      console.log(`Created cookbook: ${cookbookId}`);

      if (!quota.isPremium) {
        const outcome = await confirmCookbookDeckNotRemovedByQuotaTrigger(cookbookRef);
        if (!outcome.ok && outcome.removedByMonthlyLimit) {
          throw new Error(cookbookDeckRemovedByMonthlyLimitMessage);
        }
      }

      // Create cards for each code snippet from the course
      const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
      let cardCount = 0;

      // Use allLessons (loaded from Firestore) if available, otherwise fall back to course.syllabus
      const lessonsToProcess = allLessons.length > 0 ? allLessons : (course.syllabus || []);

      if (Array.isArray(lessonsToProcess)) {
        for (const lesson of lessonsToProcess) {
          if (lesson.subLessons && Array.isArray(lesson.subLessons)) {
            for (const subLesson of lesson.subLessons) {
              if (subLesson.codeSnippet && subLesson.codeSnippet.trim()) {
                const cardData = {
                  title: `${lesson.title} - ${subLesson.title}`,
                  description: subLesson.description || `Code example from ${lesson.title}`,
                  solution: subLesson.codeSnippet,
                  tags: [courseTopic, course.difficulty, lesson.title.toLowerCase().replace(/\s+/g, '_')].filter(tag => tag !== undefined && tag !== null),
                  // Course-specific metadata
                  courseId: course.id,
                  lessonId: lesson.id,
                  subLessonId: subLesson.id,
                  courseTitle: course.title,
                  lessonTitle: lesson.title,
                  subLessonTitle: subLesson.title,
                  // Initialize leveling fields
                  level: 1,
                  current_exp: 0,
                  exp_to_level: calculateExpToLevel(2),
                  currentLineIndex: 0,
                  // Timestamps
                  createdAt: timestamp,
                  updatedAt: timestamp,
                  creationSource: 'course',
                };

                await addDoc(cardsRef, cardData);
                cardCount++;
                console.log(`Created card for: ${subLesson.title}`, {
                  lessonId: lesson.id,
                  subLessonId: subLesson.id,
                  title: cardData.title,
                  hasCodeSnippet: !!subLesson.codeSnippet
                });
              }
            }
          }
        }
      }

      console.log(`Successfully created cookbook with ${cardCount} cards`);
      return cookbookId;

    } catch (error) {
      console.error('Error creating course cookbook:', error);
      throw error;
    }
  };

  // Helper function to ensure course deck exists with all snippet references
  const ensureCourseDeckExists = async () => {
    try {
      // Check if course deck already exists
      const decksRef = collection(db, `users/${userId}/decks`);
      const q = query(decksRef, where('courseId', '==', course.id), where('type', '==', 'cookbook'));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Return existing deck ID
        return querySnapshot.docs[0].id;
      }

      // Create new course deck using the same approach as LearnMode.js
      console.log('Creating new course deck...');
      const courseDeckId = await createCookbookFromCourse(course);

      if (onDeckCreated) {
        onDeckCreated(courseDeckId, { name: `${course.title} - Code Cookbook` });
      }

      return courseDeckId;
    } catch (error) {
      console.error('Error ensuring course deck exists:', error);
      throw error;
    }
  };


  // Helper function to find specific snippet reference in deck
  const findSnippetReferenceInDeck = async (deckId, subLessonId, lessonId) => {
    try {
      const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);

      // First try to match by both lessonId and subLessonId for more precise matching
      let q = query(cardsRef, where('lessonId', '==', lessonId), where('subLessonId', '==', subLessonId));
      let querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const cardDoc = querySnapshot.docs[0];
        console.log('Found snippet by lessonId + subLessonId:', cardDoc.data().title);
        return { id: cardDoc.id, ...cardDoc.data() };
      }

      // Fallback: try to match by subLessonId only
      q = query(cardsRef, where('subLessonId', '==', subLessonId));
      querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const cardDoc = querySnapshot.docs[0];
        console.log('Found snippet by subLessonId only:', cardDoc.data().title);
        return { id: cardDoc.id, ...cardDoc.data() };
      }

      // Last resort: try to match by code snippet content
      if (currentSubLesson && currentSubLesson.codeSnippet) {
        const allCardsSnapshot = await getDocs(cardsRef);
        for (const cardDoc of allCardsSnapshot.docs) {
          const cardData = cardDoc.data();
          if (cardData.solution && cardData.solution.trim() === currentSubLesson.codeSnippet.trim()) {
            console.log('Found snippet by content match:', cardData.title);
            return { id: cardDoc.id, ...cardData };
          }
        }
      }

      console.log('No snippet found in deck for subLessonId:', subLessonId, 'lessonId:', lessonId);
      return null;
    } catch (error) {
      console.error('Error finding snippet reference in deck:', error);
      return null;
    }
  };


  const handleStudyEntireCourse = async () => {
    try {
      // Save current lesson progress before starting study
      if (onProgressUpdate) {
        onProgressUpdate(course.id, currentLessonIndex, currentSubLessonIndex, false);
      }

      // Ensure course deck exists with proper snippet references
      const courseDeckId = await ensureCourseDeckExists();

      if (!courseDeckId) {
        alert('Failed to create course deck. Please try again.');
        return;
      }

      // Start studying the entire course deck
      if (onStartStudying) {
        onStartStudying(courseDeckId);
        // Update course level after study session
        setTimeout(() => {
          updateCourseLevel();
        }, 1000);
      } else if (onModeChange) {
        onModeChange('study');
      }
    } catch (error) {
      console.error('Error handling course study:', error);
      alert('Failed to start course study. Please try again.');
    }
  };

  const handleStudyCurrentLesson = async () => {
    try {
      // Save current lesson progress before starting study
      if (onProgressUpdate) {
        onProgressUpdate(course.id, currentLessonIndex, currentSubLessonIndex, false);
      }

      const lessonId = currentLesson?.id;
      if (!lessonId) return;

      const courseDeckId = await ensureCourseDeckExists();
      if (!courseDeckId) {
        alert('Failed to create course deck. Please try again.');
        return;
      }

      const cards = await fetchLessonCards(lessonId);
      let launchCards = cards;
      if (!launchCards.length) {
        launchCards = buildCardsFromLessonSubLessons(currentLesson);
      }
      if (!launchCards.length) {
        alert('No studyable snippets found for this lesson.');
        return;
      }

      // Add course context to cards
      const courseCards = launchCards.map(card => ({
        ...card,
        courseId: course.id,
        courseTitle: course.title,
        courseLevel: courseLevel
      }));

      if (onStartStudying) {
        onStartStudying({ deckId: courseDeckId, cards: courseCards });
        // Update course level after study session
        setTimeout(() => {
          updateCourseLevel();
        }, 1000);
      } else if (onModeChange) {
        onModeChange('study');
      }
    } catch (e) {
      console.error('Error starting study for lesson:', e);
      alert('Failed to start lesson study. Please try again.');
    }
  };

  const handlePracticeThisCode = async () => {
    if (!currentSubLesson || !currentSubLesson.codeSnippet) return;

    try {
      // Save current lesson progress before starting study
      if (onProgressUpdate) {
        onProgressUpdate(course.id, currentLessonIndex, currentSubLessonIndex, false);
      }

      // First, ensure we have a course deck with proper snippet references
      const courseDeckId = await ensureCourseDeckExists();

      if (!courseDeckId) {
        alert('Failed to create course deck. Please try again.');
        return;
      }

      // Find the specific snippet reference in the course deck
      console.log('Looking for snippet in deck:', {
        courseDeckId,
        currentLessonId: currentLesson?.id,
        currentSubLessonId: currentSubLesson.id,
        currentSubLessonTitle: currentSubLesson.title,
        hasCodeSnippet: !!currentSubLesson.codeSnippet
      });
      const snippetRef = await findSnippetReferenceInDeck(courseDeckId, currentSubLesson.id, currentLesson?.id);

      if (snippetRef) {
        console.log('✅ Found snippet reference in deck:', snippetRef.title);

        if (onStartStudying) {
          // Pass object with both deck ID and starting card ID
          const studyParams = {
            type: 'deck',
            deckId: courseDeckId,
            startCardId: snippetRef.id
          };
          console.log('🎯 Starting study with params:', studyParams);
          onStartStudying(studyParams);

          // Update course level after study session
          setTimeout(() => {
            updateCourseLevel();
          }, 1000);
        } else if (onModeChange) {
          onModeChange('study');
        }
      } else {
        console.log('❌ Snippet reference not found in deck, using fallback method');
        // Fallback to old method if snippet reference not found
        const card = {
          id: currentSubLesson.id,
          title: currentSubLesson.title || 'Practice',
          description: currentSubLesson.description || '',
          solution: currentSubLesson.codeSnippet,
          language: normalizeLanguage(currentSubLesson.language || course.language),
          tags: [],
          originalSnippetId: currentSubLesson.id,
          courseId: course.id,
          courseTitle: course.title,
          courseLevel: courseLevel
        };

        if (onStartStudying) {
          onStartStudying({ deckId: courseDeckId, cards: [card] });
          setTimeout(() => {
            updateCourseLevel();
          }, 1000);
        } else if (onModeChange) {
          onModeChange('study');
        }
      }
    } catch (error) {
      console.error('Error handling practice code:', error);
      alert('Failed to start code practice. Please try again.');
    }
  };

  // Handle flashcard flip
  const handleFlipCard = () => {
    console.log('Flipping card, current state:', {
      isFlipped,
      isExpanded,
      currentSubLesson: currentSubLesson?.title,
      hasCodeSnippet: !!currentSubLesson?.codeSnippet,
      codeSnippet: currentSubLesson?.codeSnippet?.substring(0, 50) + '...'
    });
    setIsFlipped(!isFlipped);
  };

  // Handle expand/collapse card
  const handleExpandCard = (e) => {
    e.stopPropagation(); // Prevent event bubbling to parent header
    console.log('Expanding/collapsing card, current state:', {
      isExpanded,
      isFlipped,
      currentSubLesson: currentSubLesson?.title
    });
    setIsExpanded(!isExpanded);
  };

  // Handle syllabus view toggle
  const toggleSyllabusView = () => {
    setShowSyllabusView(!showSyllabusView);
  };

  // Calculate optimal layout to prevent overlapping
  const calculateLayout = () => {
    const header = document.querySelector('.sandwich-header');
    const center = document.querySelector('.sandwich-center');
    const bottom = document.querySelector('.sandwich-bottom');
    const flashcard = document.querySelector('.flashcard');

    if (!header || !center || !bottom || !flashcard) {
      return;
    }

    const viewportHeight = window.innerHeight;
    const headerHeight = header.offsetHeight;
    const bottomHeight = isExpanded ? 0 : bottom.offsetHeight;
    const availableHeight = viewportHeight - headerHeight - bottomHeight;

    // If expanded, use full viewport height minus header
    if (isExpanded) {
      const optimalHeight = viewportHeight - headerHeight;
      setFlashcardHeight(optimalHeight);
      setLayoutCalculated(true);

      console.log('Layout calculated (expanded - full height):', {
        viewportHeight,
        headerHeight,
        optimalHeight,
        isExpanded
      });
      return;
    }

    // Calculate minimum required height for flashcard content (only for non-expanded state)
    const flashcardHeader = flashcard.querySelector('.flashcard-header');
    const flashcardContent = flashcard.querySelector('.flashcard-content');
    const flashcardActions = flashcard.querySelector('.flashcard-actions');

    let minContentHeight = 0;
    if (flashcardHeader) minContentHeight += flashcardHeader.offsetHeight;
    if (flashcardContent) minContentHeight += flashcardContent.scrollHeight;
    if (flashcardActions) minContentHeight += flashcardActions.offsetHeight;

    // Add padding and margins
    const padding = 40; // 20px top + 20px bottom
    const margins = 40; // 20px top + 20px bottom
    const totalMinHeight = minContentHeight + padding + margins;

    // Calculate optimal height with more conservative spacing
    // Reserve more space for header and bottom sections to prevent overlap
    const safetyBuffer = 150;
    const safeAvailableHeight = Math.max(availableHeight - safetyBuffer, 200);

    // Use a more generous approach - max 80% of available space
    const maxHeightMultiplier = 0.8;
    const maxHeightLimit = 600;
    const optimalHeight = Math.max(
      Math.min(safeAvailableHeight * maxHeightMultiplier, maxHeightLimit),
      Math.min(totalMinHeight, safeAvailableHeight * 0.9)
    );

    setFlashcardHeight(optimalHeight);
    setLayoutCalculated(true);

    console.log('Layout calculated (collapsed - content-based):', {
      viewportHeight,
      headerHeight,
      bottomHeight,
      availableHeight,
      minContentHeight,
      optimalHeight,
      isExpanded
    });
  };

  // Check if elements are overlapping
  const checkOverlap = () => {
    const center = document.querySelector('.sandwich-center');
    const bottom = document.querySelector('.sandwich-bottom');
    const flashcard = document.querySelector('.flashcard');
    const header = document.querySelector('.sandwich-header');

    if (!center || !bottom || !flashcard || !header) {
      return false;
    }

    const centerRect = center.getBoundingClientRect();
    const bottomRect = bottom.getBoundingClientRect();
    const flashcardRect = flashcard.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();

    // Check for any visible overlays or modals that might be overlapping
    const overlays = document.querySelectorAll('.course-settings-overlay, .import-modal-overlay, .delete-confirm-overlay, .course-settings-menu');
    let hasOverlayOverlap = false;

    overlays.forEach(overlay => {
      if (overlay.offsetParent !== null) { // Check if overlay is visible
        const overlayRect = overlay.getBoundingClientRect();
        const isOverlappingContent = overlayRect.bottom > centerRect.top && overlayRect.top < centerRect.bottom;
        if (isOverlappingContent) {
          hasOverlayOverlap = true;
          console.warn('Overlay overlapping content:', {
            overlay: overlay.className,
            overlayRect: overlayRect,
            centerRect: centerRect
          });
        }
      }
    });

    // Check if flashcard bottom overlaps with bottom section top
    const isFlashcardOverlapping = flashcardRect.bottom > bottomRect.top - 20; // 20px buffer

    // Check if flashcard top overlaps with header bottom
    const isHeaderOverlapping = flashcardRect.top < headerRect.bottom + 20; // 20px buffer

    const isOverlapping = isFlashcardOverlapping || isHeaderOverlapping || hasOverlayOverlap;

    if (isOverlapping) {
      console.warn('Elements are overlapping!', {
        flashcardBottom: flashcardRect.bottom,
        bottomTop: bottomRect.top,
        flashcardTop: flashcardRect.top,
        headerBottom: headerRect.bottom,
        flashcardOverlap: isFlashcardOverlapping,
        headerOverlap: isHeaderOverlapping,
        overlayOverlap: hasOverlayOverlap
      });
    }

    setHasOverlap(isOverlapping);

    // If there's an overlay overlap, try to close it
    if (hasOverlayOverlap) {
      console.log('Attempting to close overlapping overlays...');
      setShowSettingsMenu(false);
      setShowImportModal(false);
      setShowDeleteConfirm(false);
    }

    // If flashcard is overlapping, force a smaller height
    if (isFlashcardOverlapping || isHeaderOverlapping) {
      console.log('Forcing smaller flashcard height due to overlap...');
      const emergencyHeight = Math.min(flashcardHeight * 0.8, 400); // Reduce by 20% or max 400px
      setFlashcardHeight(emergencyHeight);
    }

    return isOverlapping;
  };

  const handleLessonSelect = (lessonIndex) => {
    setCurrentLessonIndex(lessonIndex);

    // Restore the sub-lesson index for this lesson, or default to 0
    const savedSubLessonIndex = lessonSubLessonStates[lessonIndex] || 0;
    setCurrentSubLessonIndex(savedSubLessonIndex);

    setShowHint(false);
  };

  const toggleLessonExpansion = (lessonIndex) => {
    setExpandedLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonIndex)) {
        newSet.delete(lessonIndex);
      } else {
        newSet.add(lessonIndex);
      }
      return newSet;
    });
  };

  const handleSubLessonSelect = (lessonIndex, subLessonIndex) => {
    // If clicking on a sub-lesson in a different lesson, switch to that lesson first
    if (currentLessonIndex !== lessonIndex) {
      setCurrentLessonIndex(lessonIndex);
    }

    // Update the global state for the current lesson
    setCurrentSubLessonIndex(subLessonIndex);

    // Update the per-lesson state
    setLessonSubLessonStates(prev => ({
      ...prev,
      [lessonIndex]: subLessonIndex
    }));

    setShowHint(false);
  };

  const handleNext = () => {
    if (currentSubLessonIndex < currentLesson.subLessons.length - 1) {
      // Move to next sub-lesson
      const nextSubLessonIndex = currentSubLessonIndex + 1;
      setCurrentSubLessonIndex(nextSubLessonIndex);

      // Update per-lesson state
      setLessonSubLessonStates(prev => ({
        ...prev,
        [currentLessonIndex]: nextSubLessonIndex
      }));

      onProgressUpdate(course.id, currentLessonIndex, nextSubLessonIndex, false);
    } else if (currentLessonIndex < syllabus.length - 1) {
      // Move to next lesson
      const nextLessonIndex = currentLessonIndex + 1;
      setCurrentLessonIndex(nextLessonIndex);
      setCurrentSubLessonIndex(0);

      // Update per-lesson state
      setLessonSubLessonStates(prev => ({
        ...prev,
        [nextLessonIndex]: 0
      }));


      onProgressUpdate(course.id, nextLessonIndex, 0, false);
    }
  };

  const handlePrevious = () => {
    if (currentSubLessonIndex > 0) {
      // Move to previous sub-lesson
      const prevSubLessonIndex = currentSubLessonIndex - 1;
      setCurrentSubLessonIndex(prevSubLessonIndex);

      // Update per-lesson state
      setLessonSubLessonStates(prev => ({
        ...prev,
        [currentLessonIndex]: prevSubLessonIndex
      }));

      onProgressUpdate(course.id, currentLessonIndex, prevSubLessonIndex, false);
    } else if (currentLessonIndex > 0) {
      // Move to previous lesson
      const prevLessonIndex = currentLessonIndex - 1;
      const prevLesson = syllabus[prevLessonIndex];
      const lastSubLessonIndex = prevLesson.subLessons.length - 1;

      setCurrentLessonIndex(prevLessonIndex);
      setCurrentSubLessonIndex(lastSubLessonIndex);

      // Update per-lesson state
      setLessonSubLessonStates(prev => ({
        ...prev,
        [prevLessonIndex]: lastSubLessonIndex
      }));


      onProgressUpdate(course.id, prevLessonIndex, lastSubLessonIndex, false);
    }
  };

  const handleComplete = () => {
    onProgressUpdate(course.id, currentLessonIndex, currentSubLessonIndex, true);

    // Auto-advance to next item
    setTimeout(() => {
      handleNext();
    }, 1000);
  };

  const isLessonCompleted = (lessonIndex) => {
    const lessonProgress = course.progress?.completedLessons?.[lessonIndex];
    if (!lessonProgress) return false;

    const lesson = syllabus[lessonIndex];
    return lesson.subLessons.every((_, subIndex) => lessonProgress[subIndex]);
  };

  const isSubLessonCompleted = (lessonIndex, subLessonIndex) => {
    return course.progress?.completedLessons?.[lessonIndex]?.[subLessonIndex] || false;
  };

  const generateConceptExplanation = async () => {
    if (!currentLesson || !currentSubLesson || generatingExplanation) return;

    try {
      setGeneratingExplanation(true);
      console.log('Generating streaming concept explanation for:', {
        userId,
        courseId: course.id,
        lessonId: currentLesson.id,
        subLessonId: currentSubLesson.id
      });

      const functions = getFunctions();
      const generateConceptExplanationStreaming = httpsCallable(functions, 'generateConceptExplanationForSubLessonStreaming');

      const result = await generateConceptExplanationStreaming({
        userId,
        courseId: course.id,
        lessonId: currentLesson.id,
        subLessonId: currentSubLesson.id
      });

      console.log('Streaming concept explanation result:', result);
      console.log('Result data:', result.data);
      console.log('Has codeSnippet in result:', !!result.data?.codeSnippet);

      if (result.data && result.data.success) {
        const explanationKey = `${currentLesson.id}_${currentSubLesson.id}`;
        const explanation = result.data.conceptExplanation;
        const codeSnippet = result.data.codeSnippet;

        console.log('Successfully generated explanation:', explanation.substring(0, 100) + '...');
        if (codeSnippet) {
          console.log('Successfully generated code snippet:', codeSnippet.substring(0, 100) + '...');
        } else {
          console.log('No code snippet generated by backend function');
        }

        // Update the concept explanations state
        setConceptExplanations(prev => ({
          ...prev,
          [explanationKey]: explanation
        }));

        // Update the current sub-lesson object directly
        currentSubLesson.conceptExplanation = explanation;
        if (codeSnippet) {
          currentSubLesson.codeSnippet = codeSnippet;
          currentSubLesson.language = extractCourseLanguage(course);
          console.log('Updated currentSubLesson.codeSnippet:', currentSubLesson.codeSnippet.substring(0, 100) + '...');
          console.log('Updated currentSubLesson.language:', currentSubLesson.language);
        } else {
          console.log('No code snippet to update in currentSubLesson');
        }

        // Also update the course data locally for persistence
        const updatedSubLessons = currentLesson.subLessons.map(sub =>
          sub.id === currentSubLesson.id
            ? {
              ...sub,
              conceptExplanation: explanation,
              ...(codeSnippet && {
                codeSnippet,
                language: extractCourseLanguage(course)
              })
            }
            : sub
        );

        console.log('Updated sub-lessons with code snippet:', updatedSubLessons.find(sub => sub.id === currentSubLesson.id));

        // Update the course syllabus
        const updatedSyllabus = syllabus.map(lesson =>
          lesson.id === currentLesson.id
            ? { ...lesson, subLessons: updatedSubLessons }
            : lesson
        );

        console.log('Updated syllabus with code snippet:', updatedSyllabus.find(lesson => lesson.id === currentLesson.id));

        // Update the allLessons state (this is a local update, the actual data is already updated in Firestore)
        setAllLessons(updatedSyllabus);

        console.log('Concept explanation and code snippet generated and saved successfully!');
        console.log('Final currentSubLesson after update:', currentSubLesson);
      } else {
        console.error('Function returned unsuccessful result:', result.data);
      }
    } catch (error) {
      console.error('Error generating streaming concept explanation:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      alert('Failed to generate concept explanation. Please try again.');
    } finally {
      setGeneratingExplanation(false);
    }
  };



  const getProgressPercentage = () => {
    if (!syllabus || syllabus.length === 0) return 0;

    let completedSubLessons = 0;
    let totalSubLessons = 0;

    syllabus.forEach((lesson, lessonIndex) => {
      lesson.subLessons.forEach((_, subIndex) => {
        totalSubLessons++;
        if (isSubLessonCompleted(lessonIndex, subIndex)) {
          completedSubLessons++;
        }
      });
    });

    return (completedSubLessons / totalSubLessons) * 100;
  };

  const handleDeleteCourse = async () => {
    if (onDeleteCourse) {
      try {
        await onDeleteCourse(course.id);
        setShowDeleteConfirm(false);
        setShowSettingsMenu(false);
      } catch (error) {
        console.error('Failed to delete course:', error);
      }
    }
  };

  const handleImportToDeck = async () => {
    try {
      setImporting(true);

      // Get all course snippets
      const cards = await fetchAllCourseCards();
      let launchCards = cards;

      // Fallback to sub-lesson code snippets if no snippets found
      if (!launchCards.length) {
        launchCards = buildCardsFromCourseSubLessons(allLessons);
      }

      if (!launchCards.length) {
        alert('No studyable snippets found for this course.');
        return;
      }

      const quota = await fetchCookbookSaveQuota();
      if (quota.quotaCheckUnavailable) {
        alert(messageForCookbookQuotaCheckUnavailable(quota));
        return;
      }
      if (!quota.isPremium && quota.allowed === false) {
        alert(
          `Free accounts can create up to ${quota.limit} new cookbooks per month (UTC). Upgrade to Pro for unlimited cookbooks and full features.`
        );
        return;
      }

      // First try to extract language from course title or use course.language
      const extractedCourseLanguage = extractCourseLanguage(course);

      // Determine the most common language from the cards
      const languageCounts = {};
      launchCards.forEach(card => {
        const lang = card.language || 'javascript';
        languageCounts[lang] = (languageCounts[lang] || 0) + 1;
      });

      console.log('Extracted course language:', extractedCourseLanguage);
      console.log('Language distribution in cards:', languageCounts);
      console.log('Course language:', course.language);
      console.log('Course title:', course.title);
      console.log('Sample cards:', launchCards.slice(0, 3).map(c => ({ title: c.title, language: c.language })));

      // Use extracted course language if it's found in the cards, otherwise use most common
      const mostCommonLanguage = languageCounts[extractedCourseLanguage] > 0
        ? extractedCourseLanguage
        : Object.keys(languageCounts).reduce((a, b) =>
          languageCounts[a] > languageCounts[b] ? a : b
        ) || extractedCourseLanguage;

      console.log('Selected deck language:', mostCommonLanguage);

      // Create a new deck for this course
      const timestamp = new Date().toISOString();
      const levelingFields = levelingSystem.initializeCookbookLeveling();

      const newDeck = {
        name: `${course.title} - Imported`,
        description: `Imported from course: ${course.description}`,
        language: mostCommonLanguage,
        type: 'cookbook',
        tags: [course.topic || 'course-import', mostCommonLanguage],
        createdAt: timestamp,
        updatedAt: timestamp,
        lastAccessed: timestamp,
        userId: userId,
        importedFromCourse: course.id,
        importedAt: timestamp,
        ...levelingFields
      };

      // Create the deck
      const decksRef = collection(db, `users/${userId}/decks`);
      const deckRef = await addDoc(decksRef, newDeck);

      if (!quota.isPremium) {
        const outcome = await confirmCookbookDeckNotRemovedByQuotaTrigger(deckRef);
        if (!outcome.ok && outcome.removedByMonthlyLimit) {
          alert(cookbookDeckRemovedByMonthlyLimitMessage);
          return;
        }
      }

      // Add all cards to the deck
      const cardsRef = collection(db, `users/${userId}/decks/${deckRef.id}/cards`);
      const cardPromises = launchCards.map(card => {
        const cardLevelingFields = levelingSystem.initializeSnippetLeveling();
        return addDoc(cardsRef, {
          title: card.title,
          description: card.description,
          solution: card.solution,
          language: card.language,
          tags: card.tags || [],
          createdAt: timestamp,
          updatedAt: timestamp,
          originalSnippetId: card.originalSnippetId,
          importedFromCourse: course.id,
          creationSource: 'course',
          ...cardLevelingFields
        });
      });

      await Promise.all(cardPromises);

      alert(`Successfully imported ${launchCards.length} snippets to deck "${newDeck.name}"!`);
      setShowImportModal(false);

      // Notify parent component that a deck was created
      if (onDeckCreated) {
        onDeckCreated(deckRef.id, newDeck);
      }

    } catch (error) {
      console.error('Error importing course to deck:', error);
      alert('Failed to import course to deck. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleAddTag = async () => {
    const raw = (newTag || '').trim();
    if (!raw) return;
    const tag = raw.toLowerCase().replace(/\s+/g, '_');
    try {
      setAddingTag(true);
      const courseRef = doc(db, `users/${userId}/courses/${course.id}`);
      await updateDoc(courseRef, { tags: arrayUnion(tag), updatedAt: new Date() });
      // update local object so UI reflects immediately
      course.tags = Array.isArray(course.tags) ? Array.from(new Set([...course.tags, tag])) : [tag];
      setNewTag('');
    } catch (e) {
      console.error('Failed to add tag', e);
      alert('Failed to add tag. Please try again.');
    } finally {
      setAddingTag(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    try {
      const courseRef = doc(db, `users/${userId}/courses/${course.id}`);
      // Remove tag from Firestore
      await updateDoc(courseRef, { tags: (course.tags || []).filter(t => t !== tag), updatedAt: new Date() });
      // Update local
      course.tags = (course.tags || []).filter(t => t !== tag);
      setTagMenu({ open: false, tag: '', rename: '' });
    } catch (e) {
      console.error('Failed to delete tag', e);
      alert('Failed to delete tag. Please try again.');
    }
  };

  const handleRenameTag = async (oldTag, newName) => {
    const raw = (newName || '').trim();
    if (!raw || raw === oldTag) {
      setTagMenu({ open: false, tag: '', rename: '' });
      return;
    }
    const nextTag = raw.toLowerCase().replace(/\s+/g, '_');
    try {
      const nextTags = (course.tags || []).map(t => (t === oldTag ? nextTag : t));
      const courseRef = doc(db, `users/${userId}/courses/${course.id}`);
      await updateDoc(courseRef, { tags: nextTags, updatedAt: new Date() });
      course.tags = nextTags;
      setTagMenu({ open: false, tag: '', rename: '' });
    } catch (e) {
      console.error('Failed to rename tag', e);
      alert('Failed to rename tag. Please try again.');
    }
  };

  const toggleSettingsMenu = (e) => {
    e.stopPropagation();
    setShowSettingsMenu(!showSettingsMenu);
  };

  const closeSettingsMenu = () => {
    setShowSettingsMenu(false);
  };

  // Debug logging
  console.log('CourseViewer - Course data:', course);
  console.log('CourseViewer - Current lesson:', currentLesson);
  console.log('CourseViewer - Current sub-lesson:', currentSubLesson);
  console.log('CourseViewer - Current sub-lesson codeSnippet:', currentSubLesson?.codeSnippet);
  console.log('CourseViewer - Syllabus:', syllabus);
  console.log('CourseViewer - All lessons loaded:', allLessons);
  console.log('CourseViewer - Current lesson index:', currentLessonIndex);
  console.log('CourseViewer - Current sub-lesson index:', currentSubLessonIndex);
  console.log('CourseViewer - First lesson:', syllabus?.[0]);
  console.log('CourseViewer - First lesson subLessons:', syllabus?.[0]?.subLessons);

  // If syllabus is missing, create a fallback structure for testing
  if (!syllabus || syllabus.length === 0) {
    console.log('CourseViewer - No syllabus found, creating fallback structure');
    const fallbackSyllabus = [
      {
        id: 'lesson_1',
        title: 'Introduction',
        description: 'Getting started with the course',
        order: 1,
        type: 'concept',
        estimatedTime: 30,
        subLessons: [
          {
            id: 'sub_1_1',
            title: 'Welcome',
            description: 'Introduction to the course',
            type: 'concept',
            order: 1,
            learningObjectives: ['Understand the course structure']
          }
        ]
      }
    ];
    setAllLessons(fallbackSyllabus);
  }

  // Fix lessons that have empty subLessons arrays
  if (syllabus) {
    syllabus.forEach((lesson, lessonIndex) => {
      if (!lesson.subLessons || lesson.subLessons.length === 0) {
        console.log(`CourseViewer - Lesson ${lessonIndex + 1} has no sub-lessons, creating fallback sub-lessons`);
        lesson.subLessons = [
          {
            id: `sub_${lesson.id}_1`,
            title: `${lesson.title} - Overview`,
            description: lesson.description || 'Learn the key concepts',
            type: 'concept',
            order: 1,
            learningObjectives: ['Understand the main concepts', 'Learn the fundamentals']
          },
          {
            id: `sub_${lesson.id}_2`,
            title: `${lesson.title} - Practice`,
            description: 'Apply what you\'ve learned',
            type: 'code_practice',
            order: 2,
            learningObjectives: ['Practice the concepts', 'Build confidence']
          }
        ];
      }
    });
  }

  if (!currentLesson || !currentLesson.subLessons || !currentSubLesson) {
    return (
      <div className="course-viewer">
        <div className="course-header">
          <div className="course-title-section">
            {(() => {
              // Use the same language extraction logic as the import function
              const extractedLanguage = extractCourseLanguage(course);
              const displayLanguage = extractedLanguage === 'javascript' ? 'JavaScript' :
                extractedLanguage === 'python' ? 'Python' :
                  extractedLanguage === 'java' ? 'Java' :
                    extractedLanguage === 'cpp' ? 'C++' :
                      extractedLanguage === 'csharp' ? 'C#' :
                        extractedLanguage === 'ruby' ? 'Ruby' :
                          extractedLanguage === 'typescript' ? 'TypeScript' :
                            extractedLanguage === 'go' ? 'Go' :
                              extractedLanguage === 'php' ? 'PHP' :
                                extractedLanguage === 'rust' ? 'Rust' :
                                  extractedLanguage === 'kotlin' ? 'Kotlin' :
                                    extractedLanguage === 'swift' ? 'Swift' :
                                      'JavaScript';

              return displayLanguage ? (
                <IconRenderer name={displayLanguage} type="language" className="course-language-icon" />
              ) : null;
            })()}
            <h1>{course.title}</h1>
          </div>
          <p>{course.description}</p>
          <div className="course-header-actions">
            <div className="course-progress-overview">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              <span className="progress-text">
                {Math.round(getProgressPercentage())}% Complete
              </span>
            </div>
          </div>
        </div>
        <div className="loading">
          Loading lesson...
          <br />
          <small>Debug: Syllabus length: {syllabus?.length || 0}</small>
          <br />
          <small>Debug: Current lesson index: {currentLessonIndex}</small>
          <br />
          <small>Debug: Current lesson: {currentLesson ? 'Found' : 'Not found'}</small>
          <br />
          <small>Debug: Current sub-lesson: {currentSubLesson ? 'Found' : 'Not found'}</small>
          <br />
          <small>Debug: First lesson structure: {syllabus?.[0] ? JSON.stringify(syllabus[0], null, 2) : 'No first lesson'}</small>
        </div>
      </div>
    );
  }

  // Simple derived level approximation
  const getCourseSnippetLevel = () => {
    return Math.max(1, Math.min(10, Math.floor(getProgressPercentage() / 10) + 1));
  };

  return (
    <div className={`course-viewer ${isExpanded ? 'expanded' : ''}`}>
      <div className="sandwich-header">
        <div className="header-left">
          {(() => {
            const extractedLanguage = extractCourseLanguage(course);
            const displayLanguage = extractedLanguage === 'javascript' ? 'JavaScript' :
              extractedLanguage === 'python' ? 'Python' :
                extractedLanguage === 'java' ? 'Java' :
                  extractedLanguage === 'cpp' ? 'C++' :
                    extractedLanguage === 'csharp' ? 'C#' :
                      extractedLanguage === 'ruby' ? 'Ruby' :
                        extractedLanguage === 'typescript' ? 'TypeScript' :
                          extractedLanguage === 'go' ? 'Go' :
                            extractedLanguage === 'php' ? 'PHP' :
                              extractedLanguage === 'rust' ? 'Rust' :
                                extractedLanguage === 'kotlin' ? 'Kotlin' :
                                  extractedLanguage === 'swift' ? 'Swift' :
                                    'JavaScript';
            return displayLanguage ? (
              <IconRenderer name={displayLanguage} type="language" className="course-language-icon" />
            ) : null;
          })()}
          <div className="header-titles">
            <h1 className="header-title">{course.title}</h1>
            {/* <div className="header-subtitle">{course.description}</div> */}
          </div>
        </div>
        <div className="header-right">
          <div className="header-metrics">
            <div className="progress-metric clickable" onClick={toggleSyllabusView}>
              <span className="metric-label">Progress</span>
              <span className="metric-value">{Math.round(getProgressPercentage())}%</span>
            </div>
            {/* <div className="level-metric">
              <span className="metric-label">Level</span>
              <span className="metric-value level-badge">Lv.{courseLevel}</span>
            </div> */}
            {/* <div className="exp-metric">
              <span className="metric-label">EXP</span>
              <div className="exp-bar-container">
                <div className="exp-bar">
                  <div
                    className="exp-bar-fill"
                    style={{ width: `${Math.min(100, (courseExp / courseExpToLevel) * 100)}%` }}
                  ></div>
                </div>
                <span className="exp-text">{courseExp}/{courseExpToLevel}</span>
              </div>
            </div> */}
            <button
              className="study-all-inline gamified-btn"
              onClick={handleStudyEntireCourse}
            >
              <span className="btn-icon">⚡</span>
              Study
            </button>
          </div>
        </div>
      </div>

      <div className="sandwich-center">
        <div className={`center-content ${isExpanded ? 'expanded' : ''}`}>
          {showSyllabusView ? (
            <div className="syllabus-middle-view">
              <div className="syllabus-middle-header">
                <h2>Course Syllabus</h2>
                <button className="back-to-lesson-btn" onClick={toggleSyllabusView}>
                  <span>←</span>
                  Back to Lesson
                </button>
              </div>
              <div className="syllabus-middle-content">
                {syllabus.map((lesson, lessonIndex) => {
                  const isLessonCompleted = lesson.subLessons.every((_, subIndex) => isSubLessonCompleted(lessonIndex, subIndex));
                  const completedSubLessons = lesson.subLessons.filter((_, subIndex) => isSubLessonCompleted(lessonIndex, subIndex)).length;
                  const totalSubLessons = lesson.subLessons.length;
                  const isCurrentLesson = currentLessonIndex === lessonIndex;

                  return (
                    <div key={lesson.id} className={`syllabus-lesson-card ${isCurrentLesson ? 'active' : ''} ${isLessonCompleted ? 'completed' : ''}`}>
                      <div className="lesson-card-header">
                        <div className="lesson-card-title">
                          <div className="lesson-number">{lessonIndex + 1}</div>
                          <h3>{lesson.title}</h3>
                        </div>
                        <div className="lesson-card-progress">
                          <span className="progress-text">{completedSubLessons}/{totalSubLessons} Complete</span>
                          {/* <div className="lesson-progress-bar">
                            <div
                              className="lesson-progress-fill"
                              style={{ width: `${(completedSubLessons / totalSubLessons) * 100}%` }}
                            ></div>
                          </div> */}
                        </div>
                      </div>
                      <div className="lesson-sub-lessons">
                        {lesson.subLessons.map((sub, subIndex) => {
                          const isActive = currentLessonIndex === lessonIndex && currentSubLessonIndex === subIndex;
                          const done = isSubLessonCompleted(lessonIndex, subIndex);
                          return (
                            <button
                              key={sub.id}
                              className={`sub-lesson-item ${isActive ? 'active' : ''} ${done ? 'done' : ''}`}
                              onClick={() => {
                                handleSubLessonSelect(lessonIndex, subIndex);
                                setShowSyllabusView(false); // Close syllabus view when selecting a lesson
                              }}
                            >
                              <div className="sub-lesson-number">
                                {done ? '✓' : subIndex + 1}
                              </div>
                              <div className="sub-lesson-title">{sub.title}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={`flashcard-container ${isExpanded ? 'expanded' : ''}`}>
              <div className="flashcard" style={{ height: layoutCalculated ? `${flashcardHeight}px` : '600px' }}>
                {/* Concept Side */}
                {console.log('Rendering card, isFlipped:', isFlipped, 'isExpanded:', isExpanded)}
                {!isFlipped && (
                  <div className="flashcard-side concept-side">
                    <div className="flashcard-header clickable-header" onClick={handleFlipCard} title="Click to flip to code">
                      <div className="flashcard-title-section">
                        <div className="flashcard-title-row">
                          <h3 className="lesson-title">{currentSubLesson.title}</h3>
                          {/* <div className="lesson-stats">
                          <span className="lesson-type-badge">{currentSubLesson.type?.replace('_', ' ') || 'concept'}</span>
                          <span className="lesson-difficulty-badge">{course.difficulty || 'beginner'}</span>
                        </div> */}
                        </div>
                        <div className="flashcard-progress-row">
                          <div className="lesson-progress-indicator">
                            <span className="lesson-number">Lesson {currentLessonIndex + 1}</span>
                            <span className="sub-lesson-number">Sub-lesson {currentSubLessonIndex + 1}</span>
                            {getCurrentConceptExplanation() && (() => {
                              const { cards, index, key } = getCardNavState();
                              const understood = Array.from((understoodCardsBySubLesson[key] || new Set()).values());
                              const isUnderstood = (understood || []).includes(index);
                              return (
                                <>
                                  {cards.length > 1 && (
                                    <div className="card-progress-dots" aria-label={`Card ${index + 1} of ${cards.length}`}>
                                      {cards.map((_, i) => (
                                        <span
                                          key={i}
                                          className={`dot ${i === index ? 'active' : ''} ${understood.includes(i) ? 'understood' : ''}`}
                                          onClick={() => setCurrentCardIndexBySubLesson((prev) => ({ ...prev, [key]: i }))}
                                        />
                                      ))}
                                    </div>
                                  )}
                                  {isUnderstood && (
                                    <div className="understood-indicator" title="This concept is marked as read">
                                      <span className="check-icon">✓</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flip-controls">
                        <span className="flashcard-side-label">💡 Concept</span>
                        <button
                          className="expand-btn"
                          onClick={(e) => handleExpandCard(e)}
                          title={isExpanded ? "Collapse card" : "Expand card to full width"}
                        >
                          {isExpanded ? "↗" : "⤢"}
                        </button>
                      </div>
                    </div>
                    <div className="flashcard-content">
                      {getCurrentConceptExplanation() ? (
                        (() => {
                          const { cards, index, key } = getCardNavState();
                          const understood = Array.from((understoodCardsBySubLesson[key] || new Set()).values());
                          const current = cards[index];
                          return (
                            <div className="concept-cards minimal">
                              <div className="concept-card">
                                {String(current || '').includes('```') ? (
                                  <pre className="card-pre">{current}</pre>
                                ) : (
                                  <p>{current}</p>
                                )}
                              </div>
                              {/* Navigation arrows moved to flashcard-actions */}
                            </div>
                          );
                        })()
                      ) : (
                        <div className="concept-placeholder">
                          <p>{generatingExplanation ? 'Generating explanation...' : 'Loading explanation...'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Code Side */}
                {console.log('Rendering code side, isFlipped:', isFlipped, 'isExpanded:', isExpanded)}
                {isFlipped && (
                  <div className="flashcard-side code-side">
                    <div className="flashcard-header clickable-header" onClick={handleFlipCard} title="Click to flip to concept">
                      <div className="flashcard-title-section">
                        <div className="flashcard-title-row">
                          <h3 className="lesson-title">{currentSubLesson.title}</h3>
                          {/* <div className="lesson-stats">
                          <span className="lesson-type-badge">{currentSubLesson.type?.replace('_', ' ') || 'concept'}</span>
                          <span className="lesson-difficulty-badge">{course.difficulty || 'beginner'}</span>
                        </div> */}
                        </div>
                        <div className="flashcard-progress-row">
                          <div className="lesson-progress-indicator">
                            <span className="lesson-number">Lesson {currentLessonIndex + 1}</span>
                            <span className="sub-lesson-number">Sub-lesson {currentSubLessonIndex + 1}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flip-controls">
                        <span className="flashcard-side-label">⚡ Code</span>
                        <button
                          className="expand-btn"
                          onClick={(e) => handleExpandCard(e)}
                          title={isExpanded ? "Collapse card" : "Expand card to full width"}
                        >
                          {isExpanded ? "↗" : "⤢"}
                        </button>
                      </div>
                    </div>
                    <div className="flashcard-content">
                      {currentSubLesson?.codeSnippet ? (
                        <div className="code-snippet-container">
                          {/* <div className="code-header">
                          <span className="code-language">{(ensureSubLessonLanguage(currentSubLesson).language || 'javascript').toUpperCase()}</span>
                          <button className="copy-code-btn" onClick={() => navigator.clipboard.writeText(currentSubLesson.codeSnippet)}>
                            📋 Copy
                          </button>
                        </div> */}
                          <pre className="code-content">
                            <code>{currentSubLesson.codeSnippet}</code>
                          </pre>
                        </div>
                      ) : (
                        <div className="no-code-placeholder">
                          <p>No code snippet available for this lesson.</p>
                          <div className="debug-info">
                            <small>Debug: codeSnippet = {currentSubLesson?.codeSnippet ? 'exists' : 'missing'}</small>
                            <br />
                            <small>Type: {currentSubLesson?.type}</small>
                            <br />
                            <small>ID: {currentSubLesson?.id}</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>


              {/* Action buttons - positioned differently based on expanded state */}
              <div className={`flashcard-actions ${isExpanded ? 'expanded' : ''}`}>
                {getCurrentConceptExplanation() && (() => {
                  const { cards, index, key } = getCardNavState();
                  const understood = Array.from((understoodCardsBySubLesson[key] || new Set()).values());
                  return (
                    <>
                      {cards.length > 1 && (
                        <button className="nav-button prev" onClick={goToPrevCard} disabled={index === 0}>←</button>
                      )}
                      <div className="flashcard-actions-center">
                        <button
                          className="mark-understood-btn gamified-btn"
                          onClick={markCurrentCardUnderstood}
                          title={(understood || []).includes(index) ? 'Unmark as Read' : 'Mark as Read'}
                        >
                          <span className="btn-icon">{(understood || []).includes(index) ? '✓' : '📖'}</span>
                          <span className="btn-text">{(understood || []).includes(index) ? 'Unmark' : 'Mark Read'}</span>
                        </button>
                        {currentSubLesson?.codeSnippet && (
                          <button
                            className="study-all-inline gamified-btn practice-btn"
                            onClick={handlePracticeThisCode}
                            title="Practice This Code"
                          >
                            <span className="btn-icon">⚡</span>
                            <span className="btn-text">Study Code</span>
                          </button>
                        )}
                      </div>
                      {cards.length > 1 && (
                        <button className="nav-button next" onClick={goToNextCard} disabled={index === cards.length - 1}>→</button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="sandwich-bottom">
        <div className="bottom-nav">
          <button onClick={handlePrevious} disabled={currentLessonIndex === 0 && currentSubLessonIndex === 0} className="nav-button prev gamified-btn">
            <span className="btn-icon">←</span>
            Previous
          </button>
          <div className="lesson-progress">
            <div className="progress-info">
              <span className="progress-text">Lesson {currentLessonIndex + 1} of {syllabus.length}</span>
              <span className="progress-separator">•</span>
              <span className="progress-text">Sub-lesson {currentSubLessonIndex + 1} of {currentLesson.subLessons.length}</span>
            </div>
            <div className="progress-bar-mini">
              <div
                className="progress-fill-mini"
                style={{ width: `${((currentLessonIndex * currentLesson.subLessons.length + currentSubLessonIndex + 1) / (syllabus.length * currentLesson.subLessons.length)) * 100}%` }}
              ></div>
            </div>
          </div>
          <button onClick={handleNext} disabled={currentLessonIndex === syllabus.length - 1 && currentSubLessonIndex === currentLesson.subLessons.length - 1} className="nav-button next gamified-btn">
            <span className="btn-icon">→</span>
            Next
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-dialog">
            <h3>Delete Course</h3>
            <p>Are you sure you want to delete "{course.title}"? This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={handleDeleteCourse}
              >
                Delete Course
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import to Deck Modal */}
      {showImportModal && (
        <div className="import-modal-overlay">
          <div className="import-modal-dialog">
            <h3>Import Course to Deck</h3>
            <p>
              This will create a new deck called "{course.title} - Imported" containing all the
              code snippets and practice exercises from this course.
            </p>
            <div className="import-modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowImportModal(false)}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                className="import-btn"
                onClick={handleImportToDeck}
                disabled={importing}
              >
                {importing ? 'Importing...' : 'Import to Deck'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close settings menu */}
      {showSettingsMenu && (
        <div className="course-settings-overlay" onClick={closeSettingsMenu}></div>
      )}
    </div>
  );
};

export default CourseViewer;
